/*
  Controller for the bespoke .luxury-lightbox (see luxury-product-lightbox.liquid).

  Open/close/focus-trap/backdrop-close all come from Dawn's shared
  ModalDialog + <product-modal> (product-modal.js, untouched). This file
  only adds what that stock modal never had for this section: prev/next
  navigation, a clickable thumbnail strip, and click-to-zoom.

  Deliberately avoids MutationObserver. This store has a third-party Terms
  & Conditions app script that runs its own MutationObserver on the entire
  document.body (childList/subtree/attributes/characterData) and
  disconnects + reconnects itself on every batch. Opening the modal already
  produces a burst of mutations from Dawn's own code (showActiveMedia(),
  the body overflow-hidden toggle, etc.); adding a second observer here that
  reacted to those same mutations created a feedback loop between the two
  observers that froze the page. Instead, this hooks the exact clicks that
  change which media item is active and updates thumbnail highlighting +
  zoom state directly, with no observer involved.
*/
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.luxury-lightbox').forEach(initLuxuryLightbox);
});

function initLuxuryLightbox(lightbox) {
  const content = lightbox.querySelector('.luxury-lightbox__content');
  if (!content) return;

  const prevButton = lightbox.querySelector('[data-lightbox-prev]');
  const nextButton = lightbox.querySelector('[data-lightbox-next]');
  const thumbs = Array.from(lightbox.querySelectorAll('.luxury-lightbox__thumb'));

  // ModalDialog (product-modal.js, shared/untouched) closes on ANY pointerup
  // inside a .media-modal whose target isn't within `deferred-media,
  // product-model` — meant as a generic "click outside the video/model
  // player to close" rule, but our content is plain <img> elements, so
  // every single click anywhere (image, arrows, thumbnails) matched that
  // "outside" case and closed the modal instantly. Stopping propagation on
  // the actual interactive surface keeps that outer close-on-click-outside
  // behavior working for genuine backdrop clicks (the empty space around
  // the centered stage/thumbnails) while leaving clicks on the image,
  // arrows, and thumbnails alone to zoom/navigate as intended.
  [lightbox.querySelector('.luxury-lightbox__stage'), lightbox.querySelector('.luxury-lightbox__thumbs')].forEach(
    (el) => {
      if (el) el.addEventListener('pointerup', (event) => event.stopPropagation());
    }
  );

  const ZOOM_SCALE = 2.2;
  // Must stay in sync with the `320ms` transition on
  // .luxury-lightbox__content > img.active/.is-leaving in
  // component-luxury-lightbox.css.
  const SLIDE_MS = 320;

  // Pins `el` to `fromTransform` with no transition, forces the browser to
  // actually commit that as its current rendered state (not just queue it
  // as the start of an already-in-flight transition to `fromTransform`),
  // then on the next frame releases it to `toTransform`, letting the
  // element's own stylesheet `transition: transform` animate the full
  // visible distance. Skipping the reflow-forcing step here matters: a
  // plain "set A, then rAF-set B" without it lets the *existing*
  // transition (transform already has one, e.g. for the slide) treat A as
  // a new mid-flight target rather than a settled value, so by the next
  // frame it's barely moved off the original transform — retargeting to B
  // then travels almost no visible distance instead of a real pop.
  // Tracks the rAF scheduled below so resetZoom() can cancel it — see the
  // comment there for why an uncancelled one is dangerous.
  let popRafId = null;

  function snapThenRelease(el, fromTransform, toTransform) {
    el.style.transition = 'none';
    el.style.transform = fromTransform;
    void el.offsetWidth;
    el.style.transition = '';
    popRafId = requestAnimationFrame(() => {
      popRafId = null;
      el.style.transform = toTransform;
    });
  }

  const items = () => Array.from(content.children);

  // Computes the same box size object-fit: contain would settle on for
  // `img` within the stage — but from its width/height HTML attributes
  // (present immediately, before the browser has any decoded pixels to
  // work with) rather than the image's own resolved intrinsic size. Used
  // to explicitly size the two images involved in a slide (see activate()):
  // left to the stylesheet's width/height: auto instead, a still-loading
  // image (loading="lazy" — nothing fetches until a slide makes it
  // non-display:none, so this is the ordinary case, not a rare one)
  // collapses to a near-zero box for however long it takes to arrive,
  // instead of reserving its real footprint up front.
  function fitSlideSize(img) {
    const naturalW = img.naturalWidth || parseInt(img.getAttribute('width'), 10) || 0;
    const naturalH = img.naturalHeight || parseInt(img.getAttribute('height'), 10) || 0;
    if (!naturalW || !naturalH) return null;
    const maxWidth = content.clientWidth;
    const maxHeight = parseFloat(getComputedStyle(content).maxHeight);
    const ratio = naturalW / naturalH;
    let width = maxWidth;
    let height = maxWidth / ratio;
    if (height > maxHeight) {
      height = maxHeight;
      width = maxHeight * ratio;
    }
    return { width, height };
  }

  function resetZoom(item) {
    // syncFromActive() calls this on every OTHER item every time the active
    // image changes — including the one just leaving, which by then already
    // has its own explicit width/height from fitSlideSize() (see activate())
    // for the slide currently in flight. Only actually clearing width/
    // height/transform when the item was zoomed keeps that call from
    // wiping fitSlideSize()'s work off an item that was never zoomed in the
    // first place; when it WAS zoomed, activate() sets a fresh slide-fit
    // size right after this runs anyway, so nothing is lost either way.
    const wasZoomed = item.classList.contains('is-zoomed');
    item.classList.remove('is-zoomed');
    if (wasZoomed) {
      item.style.width = '';
      item.style.height = '';
      item.style.transform = '';
    }
    // A still-pending snapThenRelease rAF (e.g. the zoom pop-in/pop-out,
    // scheduled a click ago and not yet due) would otherwise fire AFTER
    // this and set its own inline transform on this exact element —
    // completely overriding whatever the caller is about to do with it
    // (most importantly activate() sliding it away via a CSS class-based
    // translateX: an inline transform always wins over a class rule, so
    // the slide would silently do nothing and the image would just sit on
    // screen, fully visible, for the rest of the slide's duration before
    // vanishing all at once the moment is-leaving gets cleared). Cancelling
    // it here — resetZoom is the one call every path that touches zoom
    // state already goes through — closes that off everywhere at once.
    if (popRafId !== null) {
      cancelAnimationFrame(popRafId);
      popRafId = null;
    }
  }

  function syncFromActive() {
    const list = items();
    const active = list.find((el) => el.classList.contains('active')) || list[0];
    if (!active) return;

    const mediaId = active.dataset.mediaId;
    thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.target === mediaId));

    const activeThumb = thumbs.find((thumb) => thumb.dataset.target === mediaId);
    if (activeThumb) activeThumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });

    list.forEach((el) => {
      if (el !== active) resetZoom(el);
    });
  }

  let slideTimeoutId = null;
  let slideLeavingEl = null;
  // Set when activate() is asked to start a new slide while one is already
  // in flight. Rather than interrupting the running one (see the comment on
  // the is-sliding check in activate() below for why that used to cause a
  // visible ghost), the request waits here and runs the instant the current
  // slide's own cleanup fires. Only the most recent request is kept — a
  // burst of clicks collapses to a single extra step once things settle,
  // not one step per click.
  let pendingActivate = null;

  // The outgoing image's own transitionend (below) is what actually tells us
  // it has finished sliding off-screen; this listener is what wires that up
  // for whichever element is currently leaving.
  function onSlideTransitionEnd(event) {
    if (event.target !== slideLeavingEl || event.propertyName !== 'transform') return;
    finishSlide();
    runPendingActivate();
  }

  // Cleans up after a slide that has genuinely finished (its own
  // transitionend fired, or the fallback timeout below did). Since activate()
  // now queues a new request instead of ever interrupting one already in
  // flight (see there), this only ever runs once the outgoing image has
  // actually finished leaving — never mid-transition — so stripping its
  // is-leaving class here (dropping it to display: none via the stylesheet's
  // :not(.active):not(.is-leaving) rule) never hides anything still visibly
  // on-screen.
  function finishSlide() {
    if (slideTimeoutId !== null) {
      window.clearTimeout(slideTimeoutId);
      slideTimeoutId = null;
    }
    if (slideLeavingEl) {
      slideLeavingEl.removeEventListener('transitionend', onSlideTransitionEnd);
      slideLeavingEl = null;
    }
    // Deliberately NOT clearing the explicit width/height fitSlideSize() set
    // on these two elements when the slide started, even though the slide
    // is over. It's tempting to revert to the stylesheet's own width/height:
    // auto now that nothing needs to be absolutely positioned any more — but
    // "revert to auto" means re-running the exact intrinsic-size resolution
    // that collapses an still-loading image in the first place. If the now-
    // settled active image happens to still not be loaded at the instant
    // the slide's transitionend fires (a slow connection, easily), clearing
    // its explicit size here would flash it back to a near-zero box for
    // whatever's left of its load time — the same bug fitSlideSize exists to
    // prevent, just moved a few hundred ms later. fitSlideSize() computes
    // the exact size CSS auto-sizing would settle on anyway once the image
    // has loaded, so there's nothing to gain from reverting to auto and a
    // real gap it can reintroduce — leaving it set is simply correct.
    items().forEach((el) => el.classList.remove('is-leaving', 'is-entering'));
    content.classList.remove('is-sliding');
    content.style.height = '';
  }

  function runPendingActivate() {
    if (!pendingActivate) return;
    const { target, direction } = pendingActivate;
    pendingActivate = null;
    activate(target, direction);
  }

  // Matches the main product carousel's own motion: the outgoing image
  // visibly slides away while the incoming one slides in from the
  // opposite side, both traveling the full width, rather than either an
  // opacity crossfade or a small in-place nudge.
  function activate(target, direction = 0) {
    if (!target) return;
    const current = items().find((el) => el.classList.contains('active'));
    if (current === target) return;

    // The stylesheet's prefers-reduced-motion override sets transition: none
    // on these images, for users who've asked for that — which also means
    // the outgoing image's transitionend (below) never fires. Without a real
    // transition to key off, the slide branch below would leave it sitting
    // completely still, fully visible in its original spot, for the entire
    // fallback timeout before hiding it outright — a static freeze followed
    // by a hard pop, i.e. exactly the "lingers, then disappears all at once"
    // bug, just on a timer instead of a stale transition. Skipping straight
    // to the same instant, non-animated swap used below when nothing is
    // active yet sidesteps that: there's never a transitional state for
    // these users to see freeze, so nothing can visibly linger.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!direction || !current || reducedMotion) {
      finishSlide();
      items().forEach((el) => el.classList.remove('active', 'is-leaving'));
      target.classList.add('active');
      syncFromActive();
      return;
    }

    // A slide is already animating away. This used to call finishSlide()
    // unconditionally right here, which strips is-leaving from whatever's
    // still mid-transition with no transition of its own — instantly
    // snapping it to display: none wherever it happened to be (often still
    // most of the way on-screen, since a click/keypress arriving well under
    // SLIDE_MS after the last one is completely normal browsing speed, not
    // an edge case). That's exactly what read as "part of the previous
    // image lingers, then disappears all at once": the image never got to
    // finish sliding away, it just popped out of existence mid-flight.
    // Queuing this request instead and letting the current slide actually
    // finish (its own transitionend/fallback below still runs normally)
    // guarantees every image that starts leaving is always still around
    // long enough to actually finish leaving.
    if (content.classList.contains('is-sliding')) {
      pendingActivate = { target, direction };
      return;
    }

    resetZoom(current);
    // Both images become position: absolute for the duration of the slide
    // (see the .is-sliding rules in the stylesheet), taking them out of
    // normal flow. .luxury-lightbox__content has no explicit height of its
    // own — only a max-height ceiling — so with neither image left in flow
    // to measure, it collapsed to 0 for the entire 320ms of every single
    // slide, then snapped back the instant it ended: the whole stage
    // visibly flattened to a sliver and popped back on every image change.
    // Locking it to its own already-rendered height for the duration keeps
    // it stable through the transition.
    content.style.height = `${content.getBoundingClientRect().height}px`;
    // Explicit, immediately-known sizing for the two images about to slide
    // — see fitSlideSize() for why this can't just be left to the
    // stylesheet's width/height: auto for the entering image specifically.
    // current is normally already loaded/correctly sized (it's the one the
    // visitor is currently looking at), but sizing it too costs nothing and
    // keeps both sides of the slide on the exact same footing.
    [current, target].forEach((img) => {
      const size = fitSlideSize(img);
      if (size) {
        img.style.width = `${size.width}px`;
        img.style.height = `${size.height}px`;
      }
    });
    content.style.setProperty('--lightbox-slide-dir', direction > 0 ? '1' : '-1');
    content.classList.add('is-sliding');
    current.classList.remove('active');
    current.classList.add('is-leaving');
    target.classList.add('active', 'is-entering');
    syncFromActive();

    requestAnimationFrame(() => {
      target.classList.remove('is-entering');
    });

    // The is-leaving class (and its transform transition) gets added above
    // in the same synchronous tick as is-sliding — but the transition
    // itself only actually starts on the NEXT paint frame, same as any
    // style change. A plain `setTimeout(..., SLIDE_MS)` counts SLIDE_MS
    // from the synchronous call instead, so it fired about one frame
    // before the outgoing image had actually finished sliding off-screen:
    // is-leaving got stripped (snapping it to display: none via the
    // `:not(.active):not(.is-leaving)` rule) while a sliver of it was
    // still visibly mid-slide, reading as a lingering ghost that vanished
    // all at once rather than sliding fully away. Listening for the
    // image's own transitionend fires exactly when the slide is actually
    // done, however long that really took. The timeout is only a fallback
    // for when transitionend never fires at all (prefers-reduced-motion
    // sets transition: none on these images, so there'd be nothing to
    // transition) — padded past SLIDE_MS so it never wins the race against
    // a real transition, only steps in when there isn't one.
    slideLeavingEl = current;
    current.addEventListener('transitionend', onSlideTransitionEnd);
    slideTimeoutId = window.setTimeout(() => {
      finishSlide();
      runPendingActivate();
    }, SLIDE_MS + 100);
  }

  function step(direction) {
    const list = items();
    if (!list.length) return;
    const activeIndex = list.findIndex((el) => el.classList.contains('active'));
    const nextIndex = (((activeIndex === -1 ? 0 : activeIndex) + direction) % list.length + list.length) % list.length;
    activate(list[nextIndex], direction);
  }

  function goTo(mediaId) {
    const list = items();
    const target = list.find((el) => el.dataset.mediaId === mediaId);
    if (!target) return;
    const activeIndex = list.findIndex((el) => el.classList.contains('active'));
    const targetIndex = list.indexOf(target);
    const direction = targetIndex === activeIndex ? 0 : targetIndex > activeIndex ? 1 : -1;
    activate(target, direction);
  }

  if (prevButton) prevButton.addEventListener('click', () => step(-1));
  if (nextButton) nextButton.addEventListener('click', () => step(1));

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => goTo(thumb.dataset.target));
  });

  content.addEventListener('click', (event) => {
    const img = event.target.closest('img');
    if (!img || img.parentElement !== content) return;

    if (img.classList.contains('is-zoomed')) {
      resetZoom(img);
      // The un-zoomed image never needs scrolling (it's centered via
      // margin: auto and fits entirely within the box), so its correct
      // resting scroll position is always (0, 0) — but the decorative pop
      // below overshoots to scale(1.06) before easing to 1, and browsers
      // include an element's live transform in its scrollable ancestor's
      // overflow calculation. That made this container's OWN scrollTop/
      // scrollLeft, still sitting wherever the user had panned to while
      // zoomed, only get reclamped down toward 0 gradually as the
      // overshoot eased away — for the whole pop, the visible crop was a
      // moving window into the image instead of the image's true resting
      // position, which looked like it briefly rendered a bit off from
      // where it belonged (no rounded corner in view since the real top
      // edge wasn't what was showing yet) before "correcting" itself.
      // Forcing it to 0 up front sidesteps that: 0 is always in range
      // regardless of how the overshoot moves the transient max, so it
      // just stays there instead of being dragged along.
      content.scrollLeft = 0;
      content.scrollTop = 0;
      // Subtle pop-back cue mirroring the pop-in below: the real size is
      // already back to normal (resetZoom, instant), this transform is
      // purely decorative on top of it. It overshoots past scale(1) on
      // its way down from 1.06, which briefly reads as real overflow to
      // the browser's scrollable-area calculation (unlike the pop-in,
      // which eases up from 0.94 and never exceeds the box) — suppressing
      // overflow just for this window keeps that from flashing a
      // scrollbar for the length of the pop.
      content.classList.add('is-popping-back');
      window.setTimeout(() => content.classList.remove('is-popping-back'), SLIDE_MS);
      snapThenRelease(img, 'scale(1.06)', 'scale(1)');
      return;
    }

    // Real pixel width/height rather than `transform: scale()`: a
    // transform enlarges the image purely visually, without changing the
    // box .luxury-lightbox__content's `overflow: auto` computes its
    // scrollable area from — so the scrollbar's notion of "the top/edge
    // of the image" didn't match what was actually drawn, and panning
    // near an edge could visibly snap. Resizing the box for real keeps
    // the scrollable area and the rendered image in agreement.
    const rect = img.getBoundingClientRect();
    const xRatio = (event.clientX - rect.left) / rect.width;
    const yRatio = (event.clientY - rect.top) / rect.height;
    const zoomedWidth = rect.width * ZOOM_SCALE;
    const zoomedHeight = rect.height * ZOOM_SCALE;

    img.classList.add('is-zoomed');
    img.style.width = `${zoomedWidth}px`;
    img.style.height = `${zoomedHeight}px`;

    // Center the viewport on the clicked point right away, in the same
    // tick as the resize above — the browser clamps this to the valid
    // scroll range on its own, so a click near an edge correctly settles
    // on that true edge instead of overshooting. Deferring this to a
    // later frame (e.g. to line up with the decorative pop below) would
    // let the flex layout still be at its old, smaller size when the
    // scroll target is computed, undershooting the intended position.
    content.scrollLeft = zoomedWidth * xRatio - content.clientWidth / 2;
    content.scrollTop = zoomedHeight * yRatio - content.clientHeight / 2;

    // Purely decorative pop-in, layered on top of the already-correct
    // instant resize/scroll above: snap to slightly-smaller first, then
    // let the existing `transition: transform` on img.active ease it up
    // to full size. Scale is a transform, so unlike animating
    // width/height directly it doesn't touch the scrollable area
    // computed above, avoiding a repeat of the width/height-transition
    // timing bug this replaced.
    snapThenRelease(img, 'scale(0.94)', 'scale(1)');
  });

  lightbox.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') step(1);
    if (event.key === 'ArrowLeft') step(-1);
  });

  // Every button that opens THIS modal (main gallery images + thumbnail
  // rail, both rendered by Dawn's shared product-thumbnail.liquid via a
  // <modal-opener data-modal="#id">) already has Dawn's own click listener
  // bound directly to its inner <button data-media-id> (see ModalOpener in
  // global.js). Listeners on the same element for the same event fire in
  // registration order, so binding another listener here — after
  // product-modal.js has already run — is guaranteed to run after Dawn's
  // show()/showActiveMedia() has already set the correct .active item.
  document.querySelectorAll(`[data-modal="#${lightbox.id}"] button[data-media-id]`).forEach((button) => {
    button.addEventListener('click', syncFromActive);
  });
}
